import { Corner, Root, Viewport } from '@radix-ui/react-scroll-area'
import { cn, VisuallyHidden } from '@seen/ui'
import { Command as CommandPrimitive } from 'cmdk'
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type HTMLAttributes,
  type JSX,
  type MouseEvent,
  type Ref,
  useCallback,
  useRef,
} from 'react'
import { useNavigate } from 'react-router'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { ScrollBar } from '#src/components/core/scroll-area/ScrollBar.tsx'

export const Command = forwardRef<
  ElementRef<typeof CommandPrimitive>,
  ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, forwardedRef) => (
  <CommandPrimitive
    ref={forwardedRef}
    className={cn(
      'flex size-full flex-col overflow-hidden rounded-lg',
      className
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

export const CommandDialogContent = forwardRef<
  ElementRef<typeof DialogDisplay>,
  ComponentProps<typeof DialogDisplay>
>((props, forwardedRef) => {
  const { children, ...delegated } = props
  return (
    <DialogDisplay
      ref={forwardedRef}
      notScrollable
      hideCloseButton
      {...delegated}
    >
      <VisuallyHidden>
        <DialogTitle>Command Menu</DialogTitle>
      </VisuallyHidden>
      {children}
    </DialogDisplay>
  )
})
CommandDialogContent.displayName = DialogDisplay.displayName

export const CommandInput = forwardRef<
  ElementRef<typeof CommandPrimitive.Input>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, forwardedRef) => (
  <CommandPrimitive.Input
    placeholder="Search…"
    ref={forwardedRef}
    className={cn(
      'flex w-full rounded-md border-zinc-200 border-b bg-transparent p-3 text-sm outline-hidden placeholder:text-placeholder disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-800',
      className
    )}
    {...props}
  />
))
CommandInput.displayName = CommandPrimitive.Input.displayName

export interface CommandListProps extends ComponentPropsWithoutRef<
  typeof CommandPrimitive.List
> {
  // The element that scrolls is the viewport around the list, so scrolling a
  // list programmatically goes through this, not `ref`.
  viewportRef?: Ref<HTMLDivElement>
}

/* The list scrolls in a Radix scroll area rather than native overflow, so its
 * height, padding and a caller's `className` sit on the viewport. The scroll
 * area wraps cmdk's list, never the items: cmdk reorders filtered items by
 * appending them to its own list element, so they must stay its children.
 * The scroll area is built from the primitives, not `ScrollArea`, because
 * that one restores scroll from the router's location and a command list
 * can open outside a router (tests, the extension). Shown whenever the list
 * overflows, as the native bar was: a command list is driven from the
 * keyboard, so a hover-only bar would never appear. */
export const CommandList = forwardRef<
  ElementRef<typeof CommandPrimitive.List>,
  CommandListProps
>(({ className, viewportRef, ...props }, forwardedRef) => (
  <Root type="auto" className="relative w-full overflow-hidden">
    <Viewport
      ref={viewportRef}
      className={cn(
        // Radix lays the content out as a table, which lets wide children
        // escape; see ScrollArea.
        'isolate w-full rounded-[inherit] [&>div]:!block',
        'max-h-96 overflow-x-hidden p-1',
        className
      )}
    >
      <CommandPrimitive.List ref={forwardedRef} {...props} />
    </Viewport>
    <ScrollBar />
    <Corner />
  </Root>
))
CommandList.displayName = CommandPrimitive.List.displayName

export const CommandEmpty = forwardRef<
  ElementRef<typeof CommandPrimitive.Empty>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, forwardedRef) => (
  <CommandPrimitive.Empty
    ref={forwardedRef}
    className="py-6 text-center text-sm"
    {...props}
  />
))
CommandEmpty.displayName = CommandPrimitive.Empty.displayName

export const CommandGroup = forwardRef<
  ElementRef<typeof CommandPrimitive.Group>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, forwardedRef) => (
  <CommandPrimitive.Group
    ref={forwardedRef}
    className={cn(
      'overflow-hidden py-1 text-zinc-950 dark:text-zinc-50 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:text-zinc-500 dark:**:[[cmdk-group-heading]]:text-zinc-400',
      className
    )}
    {...props}
  />
))
CommandGroup.displayName = CommandPrimitive.Group.displayName

export const CommandSeparator = forwardRef<
  ElementRef<typeof CommandPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, forwardedRef) => (
  <CommandPrimitive.Separator
    ref={forwardedRef}
    className={cn('-mx-1 h-px bg-zinc-200 dark:bg-zinc-800', className)}
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

export const CommandItem = forwardRef<
  ElementRef<typeof CommandPrimitive.Item>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, forwardedRef) => (
  <CommandPrimitive.Item
    ref={forwardedRef}
    className={cn(
      'relative isolate flex cursor-default select-none items-center rounded-sm px-1.5 py-2 text-sm outline-hidden', // base
      'data-[selected=true]:text-accent-foreground', // selected
      'data-[selected=true]:after:-z-10 data-[selected=true]:after:absolute data-[selected=true]:after:inset-0 data-[selected=true]:after:rounded-md data-[selected=true]:after:bg-accent', // selected :after for highlighting
      'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50', // disabled
      className
    )}
    {...props}
  />
))
CommandItem.displayName = CommandPrimitive.Item.displayName

export const CommandLink = forwardRef<
  ElementRef<typeof CommandPrimitive.Item>,
  Omit<
    ComponentPropsWithoutRef<typeof CommandPrimitive.Item>,
    'asChild' | 'onSelect'
  > & {
    to: ComponentProps<typeof Link>['to']
    onSelect: (value: string, mouseEvent?: MouseEvent<HTMLAnchorElement>) => any
  }
>(({ children, value, to, onSelect, ...delegated }, forwardedRef) => {
  const navigate = useNavigate()
  const mouseEventRef = useRef<MouseEvent<HTMLAnchorElement>>(undefined)

  const handleSelect = useCallback(
    (value: string) => {
      if (!mouseEventRef.current) {
        void navigate(to)
      }
      onSelect?.(value, mouseEventRef.current)
      mouseEventRef.current = undefined
    },
    [onSelect, navigate, to]
  )

  return (
    <CommandItem
      ref={forwardedRef}
      value={value}
      onSelect={handleSelect}
      asChild
      {...delegated}
    >
      <Link
        to={to}
        onClick={(e) => {
          mouseEventRef.current = e
        }}
      >
        {children}
      </Link>
    </CommandItem>
  )
})
CommandLink.displayName = 'CommandLink'

export function CommandShortcut({
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return (
    <span
      className={cn(
        'ml-auto text-xs text-zinc-500 tracking-widest dark:text-zinc-400',
        className
      )}
      {...props}
    />
  )
}
CommandShortcut.displayName = 'CommandShortcut'
